/**
 * isgTaahhutnameService.js — reads what the Firma İSG Taahhütnamesi PDF needs and hands the finished file to the browser (backlog #541).
 * The page itself is drawn by isgTaahhutnamePdf.js; this file only talks to the backend and the browser.
 *
 * One company at ONE fair (the form is the company's, not the stand's: Hall ID and Booth ID list every stand it holds there):
 *   - the fair, in BOTH languages (GET /fuar/selectable?languageId=1 and =2, then GET /fuar/list for a fair the picker does not offer): name,
 *     the Start Up / Event / Dismantling dates and times (fuarSetup* / fuar* / fuarBreak*, Components/Fairs/fuarAsamalari.js) and the logo
 *     (fuarLogoUrl + fuarLogoSource). Since #718 the Start Up (Kurulum) window is the COMPANY's: GET /fuar/{id}/firmalar/{firmaId}/kurulum-saati
 *     (fuarKurulumSaatiService.js), the fair's own Kurulum only when that read fails. An English name the fair does not have comes back as the Turkish one (backend), so an English form may
 *     carry the Turkish fair name until the English name is entered.
 *   - the venue's name (fuarAlaniName of the same row; the Turkish form says "<venue> Fuar alanında").
 *   - the organizer companies' names (GET /fuar/{id}/detay-new, see getFuarOrganizatorAdlari): in the Turkish form's last paragraph and in its
 *     first footer line (a fillable field, filled in).
 *   - the company's stands (GET /firma/getFirmaStands?firmaId=&fuarId= -> [{stand_name "11a.11AF43", salon_name "11a"}]): hall + code.
 *   - the logo's bytes: the fair's public logo URL is read with fetch (UPLOADED / DEFAULT files are public with CORS open). A LEGACY logo lives
 *     on the old PHP host, which a browser may not read, so it goes through GET /fuar/{id}/logo first (backlog #661, same bytes through the
 *     API); until that endpoint exists the direct read is tried and, if the browser refuses, the form is made WITHOUT the logo and the caller is
 *     told (`logoSorunu`). A fair with no logo of its own (DEFAULT) never counts as a problem.
 */
import axios from "axios";
import { JAVA_BACKEND_API_URL, getAuthHeader } from "../Components/Contexts/BackendConstants";
import i18n from "../i18n";
import { fuarService, fuarLogoSrc, LANGUAGE } from "./fuarService";
import { fuarAsamalari } from "../Components/Fairs/fuarAsamalari";
import { getFirmaKurulumSaati, kurulumAsamasi } from "./fuarKurulumSaatiService";
import { standCodeFromName } from "./standNaming";
import { isgTaahhutnamePdfUret, isgTaahhutnameDosyaAdi, resimTuru } from "./isgTaahhutnamePdf";
import sakaLogoUrl from "../assets/images/saka-grup-logo.png";

export { ISG_TAAHHUTNAME_DILLERI } from "./isgTaahhutnamePdf";

const FONT_URL = (ad) => `${process.env.PUBLIC_URL || ""}/fonts/LiberationSans-${ad}.ttf`;

const dogalSirala = (a, b) => String(a).localeCompare(String(b), "tr", { numeric: true, sensitivity: "base" });
const benzersiz = (liste) => [...new Set(liste.filter(Boolean))];

/** GET /firma/getFirmaStands → { holAdlari, standKodlari, standlar: [{ hol, kod }] } (a stand can come back more than once, once per hall row). */
export async function getFirmaStandBilgisi(firmaId, fuarId) {
  const res = await axios.get(`${JAVA_BACKEND_API_URL}/firma/getFirmaStands`, { params: { firmaId, fuarId }, headers: getAuthHeader() });
  const satirlar = Array.isArray(res.data) ? res.data : [];
  const cift = new Map();
  for (const r of satirlar) {
    const hol = String(r.salon_name || "").trim();
    const kod = standCodeFromName(r.stand_name);
    if (hol || kod) cift.set(`${hol}|${kod}`, { hol, kod });
  }
  return {
    holAdlari: benzersiz(satirlar.map((r) => String(r.salon_name || "").trim())).sort(dogalSirala),
    standKodlari: benzersiz(satirlar.map((r) => standCodeFromName(r.stand_name))).sort(dogalSirala),
    standlar: [...cift.values()].sort((a, b) => dogalSirala(a.hol, b.hol) || dogalSirala(a.kod, b.kod)),
  };
}

/**
 * The names of the fair's organizer companies (the "Organizer" firmas of the fair form, firma_to_fuar type 2): GET /fuar/{id}/detay-new →
 * organizerCompanies[].firma_name. A fair can have several. [] when there is none or the read fails (the form then says "Fuar organizatörü").
 * The same read the staff fair form makes; a company login may make it too (checked 2026-11-01).
 */
export async function getFuarOrganizatorAdlari(fuarId) {
  try {
    const detay = await fuarService.getFuarDetail(fuarId);
    return benzersiz((detay?.organizerCompanies || []).map((c) => String(c.firma_name || c.firmaName || "").trim()));
  } catch (e) {
    return [];
  }
}

/** The fair's row (FuarListDTO) in one language: the picker's list first, the full list for a fair the picker does not offer (an archived one); null when neither has it. */
export async function fuarSatiri(fuarId, languageId) {
  const bul = (liste) => (Array.isArray(liste) ? liste.find((f) => Number(f.fuarId) === Number(fuarId)) : undefined);
  try {
    const satir = bul(await fuarService.getSelectableFuarList({ languageId }));
    if (satir) return satir;
  } catch (e) { /* fall through to the full list */ }
  try {
    return bul(await fuarService.getFuarList({ languageId })) || null;
  } catch (e) {
    return null;
  }
}

export async function dosyaBaytlari(url, istek = {}) {
  const res = await fetch(url, { mode: "cors", ...istek });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return new Uint8Array(await res.arrayBuffer());
}

/** A webp logo (the upload accepts png, jpg and webp) drawn through a canvas into PNG, because pdf-lib embeds only PNG and JPEG. */
export async function webpYiPngYap(bytes) {
  const bitmap = await createImageBitmap(new Blob([bytes], { type: "image/webp" }));
  const tuval = document.createElement("canvas");
  tuval.width = bitmap.width;
  tuval.height = bitmap.height;
  tuval.getContext("2d").drawImage(bitmap, 0, 0);
  const blob = await new Promise((tamam) => tuval.toBlob(tamam, "image/png"));
  return new Uint8Array(await blob.arrayBuffer());
}

export const webpMi = (b) => b && b.length > 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50;

/**
 * The fair logo as PNG / JPEG bytes. { logo: Uint8Array | null, logoSorunu: boolean } — `logoSorunu` only when the fair HAS a logo of its own
 * and none of the ways to read it worked.
 */
export async function fuarLogosuOku(satir) {
  if (!satir || !satir.fuarLogoUrl || satir.fuarLogoSource === "DEFAULT") return { logo: null, logoSorunu: false };
  const dogrudan = () => dosyaBaytlari(fuarLogoSrc(satir.fuarLogoUrl, satir.fuarLogo));
  // #661: the same bytes through the API (session header, CORS open); a backend without it answers 404 and the direct read is tried.
  const apiden = async () => new Uint8Array((await axios.get(`${JAVA_BACKEND_API_URL}/fuar/${satir.fuarId}/logo`, { responseType: "arraybuffer", headers: getAuthHeader() })).data);
  const yollar = satir.fuarLogoSource === "LEGACY" ? [apiden, dogrudan] : [dogrudan, apiden];
  for (const yol of yollar) {
    try {
      let bytes = await yol();
      if (webpMi(bytes)) bytes = await webpYiPngYap(bytes);
      if (resimTuru(bytes)) return { logo: bytes, logoSorunu: false };
    } catch (e) { /* the next way */ }
  }
  return { logo: null, logoSorunu: true };
}

/**
 * Everything the form is made from, for one company at one fair.
 * @returns {Promise<{veri: object, logoSorunu: boolean}>} `veri` is what isgTaahhutnamePdfUret takes.
 * @throws Error (UI language) when the fair is unknown.
 */
export async function isgTaahhutnameVerisiOku({ firmaId, firmaAdi, fuarId }) {
  const [tr, en, stand, organizatorAdlari, kurulumPenceresi] = await Promise.all([
    fuarSatiri(fuarId, LANGUAGE.TR),
    fuarSatiri(fuarId, LANGUAGE.EN),
    getFirmaStandBilgisi(firmaId, fuarId).catch(() => ({ holAdlari: [], standKodlari: [], standlar: [] })),
    getFuarOrganizatorAdlari(fuarId),
    getFirmaKurulumSaati(fuarId, firmaId).catch(() => null),
  ]);
  const fuar = tr || en;
  if (!fuar) throw new Error(i18n.t("pdf:isgTaahhutname.ui.fuarBulunamadi"));
  const { logo, logoSorunu } = await fuarLogosuOku(fuar);
  const asamalar = Object.fromEntries(fuarAsamalari(fuar).map((a) => [a.anahtar, a]));
  // #718: the Kurulum this COMPANY is told (its own window, else its roles', else the fair's: the backend's rule, #717) replaces the fair's own
  // Kurulum; Event and Dismantling stay the fair's. A read that failed (null) leaves the fair's Kurulum, as before this task.
  const kurulum = kurulumAsamasi(kurulumPenceresi, asamalar.kurulum);
  if (kurulum) asamalar.kurulum = kurulum;
  return {
    veri: {
      firmaAdi: firmaAdi || "",
      fuarAdlari: { tr: tr?.fuarName || en?.fuarName || "", en: en?.fuarName || tr?.fuarName || "" },
      fuarAlani: String(fuar.fuarAlaniName || "").trim(),
      organizatorAdlari,
      asamalar,
      holAdlari: stand.holAdlari,
      standKodlari: stand.standKodlari,
      standlar: stand.standlar,
      logo,
    },
    logoSorunu,
  };
}

const varliklarSozleri = {};

/** The font and the SAKA GRUP logo, fetched once per page load; the Turkish template also needs the bold and italic of the font. */
export function isgTaahhutneVarliklariniYukle(dil = "en") {
  if (!varliklarSozleri[dil]) {
    const fontlar = dil === "tr" ? ["Regular", "Bold", "Italic"] : ["Regular"];
    varliklarSozleri[dil] = Promise.all([...fontlar.map((ad) => dosyaBaytlari(FONT_URL(ad))), dosyaBaytlari(sakaLogoUrl)])
      .then(([font, ...kalan]) => {
        const sakaLogo = kalan.pop();
        return { font, sakaLogo, ...(dil === "tr" ? { fontKalin: kalan[0], fontItalik: kalan[1] } : {}) };
      })
      .catch((e) => { delete varliklarSozleri[dil]; throw e; });
  }
  return varliklarSozleri[dil];
}

/** Gives finished PDF bytes to the browser as a download called `dosyaAdi` (also used by the project taahhütnames, #666). */
export function pdfDosyasiniIndir(bytes, dosyaAdi) {
  const adres = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  try {
    const baglanti = document.createElement("a");
    baglanti.href = adres;
    baglanti.download = dosyaAdi;
    document.body.appendChild(baglanti);
    baglanti.click();
    document.body.removeChild(baglanti);
  } finally {
    setTimeout(() => URL.revokeObjectURL(adres), 10000);
  }
}

/** Makes the PDF in `dil` and gives it to the browser as a download. */
export async function isgTaahhutnameIndir(veri, dil) {
  const bytes = await isgTaahhutnamePdfUret(veri, dil, await isgTaahhutneVarliklariniYukle(dil));
  pdfDosyasiniIndir(bytes, isgTaahhutnameDosyaAdi(veri, dil));
}
